import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import facade from "../services/qwof/apiFacade";
import Spinner from "./Spinner.jsx";

// Rank sort order: higher number = lower in sort order
const RANK_ORDER = {
  "quaggan leader": 1,
  "quaggan officer": 2,
  "quaggan elder": 3,
  "quaggan legend": 4,
  "quaggan champ": 5,
  "quaggan elite": 6,
  "quaggan veteran": 7,
  quaggan: 8,
  "quaggan egg": 9,
  "quaggan friend": 10,
};

function getRankSortOrder(rank) {
  const rankLower = (rank || "").toLowerCase().trim();
  return RANK_ORDER[rankLower] !== undefined ? RANK_ORDER[rankLower] : 11; // Unknown ranks go last
}

function getAliasDisplayName(alias) {
  return alias?.name || alias?.aliasName || alias?.alias || alias?.displayName || alias?.value || "";
}

function getAliasTooltip(aliases) {
  if (!Array.isArray(aliases) || aliases.length === 0) {
    return "No aliases";
  }

  return aliases
    .map((alias) => {
      const name = getAliasDisplayName(alias);
      return alias?.preferred ? `${name} (preferred)` : name;
    })
    .filter(Boolean)
    .join("\n");
}

function GuildMembersTab({ guildId }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("username");
  const [sortOrder, setSortOrder] = useState("asc");

  const loadGuildMembers = async () => {
    if (!guildId) {
      setError("No guild selected");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await facade.getGuildMembers(guildId);
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.fullError || err?.message || "Unknown error";
      setError(`Failed to load guild members: ${msg}`);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGuildMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guildId]);

  // Filter members based on search term
  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const username = (member?.gw2Username || "").toLowerCase();
      const joinDate = member?.joined
        ? new Date(member.joined[0], member.joined[1] - 1, member.joined[2])
            .toLocaleDateString()
            .toLowerCase()
        : "";
      const rank = (member?.rank || "").toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      return (
        username.includes(searchLower) ||
        joinDate.includes(searchLower) ||
        rank.includes(searchLower)
      );
    });
  }, [members, searchTerm]);

  // Sort members
  const sortedMembers = useMemo(() => {
    const sorted = [...filteredMembers];

    sorted.sort((a, b) => {
      let aVal = "";
      let bVal = "";

      if (sortBy === "username") {
        aVal = (a?.gw2Username || "").toLowerCase();
        bVal = (b?.gw2Username || "").toLowerCase();
      } else if (sortBy === "rank") {
        aVal = getRankSortOrder(a?.rank);
        bVal = getRankSortOrder(b?.rank);
      } else if (sortBy === "joined") {
        aVal = a?.joined
          ? new Date(a.joined[0], a.joined[1] - 1, a.joined[2]).getTime()
          : 0;
        bVal = b?.joined
          ? new Date(b.joined[0], b.joined[1] - 1, b.joined[2]).getTime()
          : 0;
      } else if (sortBy === "aliases") {
        aVal = Array.isArray(a?.aliases) ? a.aliases.length : 0;
        bVal = Array.isArray(b?.aliases) ? b.aliases.length : 0;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredMembers, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const getSortIndicator = (field) => {
    if (sortBy !== field) return "";
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search by GW2 username, rank, or date..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid rgba(144, 224, 239, 0.5)",
            background: "rgba(8, 24, 50, 0.5)",
            color: "#cfeeff",
            fontSize: 14,
            boxSizing: "border-box",
          }}
        />
      </div>

      {loading ? (
        <Spinner compact size={64} />
      ) : error ? (
        <div style={{ color: "#ff6b6b", padding: 16, textAlign: "center" }}>
          {error}
        </div>
      ) : sortedMembers.length === 0 ? (
        <div
          style={{
            color: "rgba(207, 238, 255, 0.7)",
            padding: 16,
            textAlign: "center",
          }}
        >
          No guild members found
        </div>
      ) : (
        <div
          style={{
            overflowX: "auto",
            borderRadius: 8,
            border: "1px solid rgba(144, 224, 239, 0.3)",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 14,
            }}
          >
            <thead>
              <tr
                style={{
                  background: "rgba(8, 24, 50, 0.8)",
                  borderBottom: "1px solid rgba(144, 224, 239, 0.3)",
                }}
              >
                <th
                  onClick={() => handleSort("username")}
                  style={{
                    padding: 12,
                    textAlign: "left",
                    cursor: "pointer",
                    color: "#90e0ef",
                    fontWeight: 600,
                    userSelect: "none",
                  }}
                >
                  GW2 Username{getSortIndicator("username")}
                </th>
                <th
                  onClick={() => handleSort("rank")}
                  style={{
                    padding: 12,
                    textAlign: "left",
                    cursor: "pointer",
                    color: "#90e0ef",
                    fontWeight: 600,
                    userSelect: "none",
                  }}
                >
                  Rank{getSortIndicator("rank")}
                </th>
                <th
                  onClick={() => handleSort("joined")}
                  style={{
                    padding: 12,
                    textAlign: "left",
                    cursor: "pointer",
                    color: "#90e0ef",
                    fontWeight: 600,
                    userSelect: "none",
                  }}
                >
                  Joined{getSortIndicator("joined")}
                </th>
                <th
                  onClick={() => handleSort("aliases")}
                  style={{
                    padding: 12,
                    textAlign: "left",
                    cursor: "pointer",
                    color: "#90e0ef",
                    fontWeight: 600,
                    userSelect: "none",
                  }}
                >
                  Aliases{getSortIndicator("aliases")}
                </th>
                <th
                  style={{
                    padding: 12,
                    textAlign: "center",
                    color: "#90e0ef",
                    fontWeight: 600,
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((member, index) => {
                const aliases = Array.isArray(member?.aliases)
                  ? member.aliases
                  : [];
                const aliasCount = aliases.length;

                return (
                  <tr
                    key={member?.gw2Username || index}
                    style={{
                      borderBottom: "1px solid rgba(144, 224, 239, 0.2)",
                      background:
                        index % 2 === 0
                          ? "rgba(8, 24, 50, 0.3)"
                          : "transparent",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(144, 224, 239, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        index % 2 === 0
                          ? "rgba(8, 24, 50, 0.3)"
                          : "transparent";
                    }}
                  >
                    <td style={{ padding: 12, color: "#cfeeff" }}>
                      {member?.gw2Username || "Unknown"}
                    </td>
                    <td style={{ padding: 12, color: "#a8dadc" }}>
                      {member?.rank || "-"}
                    </td>
                    <td style={{ padding: 12, color: "#a8dadc" }}>
                      {member?.joined
                        ? new Date(
                            member.joined[0],
                            member.joined[1] - 1,
                            member.joined[2],
                          ).toLocaleDateString()
                        : "-"}
                    </td>
                    <td style={{ padding: 12, color: "#a8dadc" }}>
                      <span
                        title={getAliasTooltip(aliases)}
                        aria-label={getAliasTooltip(aliases)}
                        style={{
                          display: "inline-block",
                          background: "rgba(144, 224, 239, 0.2)",
                          padding: "4px 8px",
                          borderRadius: 4,
                          fontSize: 12,
                        }}
                      >
                        {aliasCount}
                      </span>
                    </td>
                    <td style={{ padding: 12, textAlign: "center" }}>
                      <button
                        onClick={() =>
                          navigate(
                            `/guildmember/${encodeURIComponent(member?.gw2Username)}`,
                            { state: { member } },
                          )
                        }
                        style={{
                          padding: "6px 12px",
                          borderRadius: 6,
                          border: "none",
                          background: "#62d0ff",
                          color: "#001a2e",
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: 13,
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#3ab3d9";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#62d0ff";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          color: "rgba(207, 238, 255, 0.6)",
          fontSize: 12,
        }}
      >
        Showing {sortedMembers.length} of {members.length} members
        {searchTerm && ` (filtered from search)`}
      </div>
    </div>
  );
}

export default GuildMembersTab;
